import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import Table from "react-bootstrap/Table";
import { useDispatch, useSelector } from "react-redux";
import { GLOBALTYPES } from "../redux/actions/globalTypes";
import { getDataAPI, postDataAPI } from "../utils/fetchData";
import Adminnavbar from "./adminnavbar";
import Footer from "./footer";
import LoginRequest from "./loginRequest";
import Sidenavbar from "./sidenav";

// Constants
const CONTAINER_STYLE = { backgroundColor: "#ffffff", padding: "30px" };

const DELETE_BUTTON_STYLE = {
	background: "#E23F1C",
	fontSize: "13px",
	borderRadius: "4px",
	border: "none",
};
const ERROR_STYLE = {
	visibility: "visible",
};

const SkillDevelopmentManagement = () => {
	const { auth } = useSelector((state) => state);
	const dispatch = useDispatch();

	// Modal state
	const [show, setShow] = useState(false);

	// Form state
	const [serial_number, setSerialNumber] = useState("");
	const [course_name, setCourseName] = useState("");
	const [standard, setStandard] = useState("");
	const [client_organization, setClientOrganization] = useState("");
	const [category, setCategory] = useState("");
	const [from_date, setFromDate] = useState("");
	const [to_date, setToDate] = useState("");
	const [state, setState] = useState("");
	const [total_participants, setTotalParticipants] = useState("");
	const [order, setOrder] = useState("");
	const [loading, setLoading] = useState(false);
	const [editMode, setEditMode] = useState(false);
	const [editId, setEditId] = useState(null);

	// Error state
	const [serialNumberError, setSerialNumberError] = useState("");
	const [courseNameError, setCourseNameError] = useState("");
	const [standardError, setStandardError] = useState("");
	const [clientOrganizationError, setClientOrganizationError] = useState("");
	const [categoryError, setCategoryError] = useState("");
	const [fromDateError, setFromDateError] = useState("");
	const [toDateError, setToDateError] = useState("");
	const [stateError, setStateError] = useState("");
	const [totalParticipantsError, setTotalParticipantsError] = useState("");
	const [orderError, setOrderError] = useState("");

	// Data state
	const [skills, setSkills] = useState([]);

	// Modal handlers
	const handleClose = useCallback(() => {
		setShow(false);
		setSerialNumber("");
		setCourseName("");
		setStandard("");
		setClientOrganization("");
		setCategory("");
		setFromDate("");
		setToDate("");
		setState("");
		setTotalParticipants("");
		setOrder("");
		setSerialNumberError("");
		setCourseNameError("");
		setStandardError("");
		setClientOrganizationError("");
		setCategoryError("");
		setFromDateError("");
		setToDateError("");
		setStateError("");
		setTotalParticipantsError("");
		setOrderError("");
		setEditMode(false);
		setEditId(null);
	}, []);

	const handleShow = useCallback(() => setShow(true), []);

	// Form validation
	const validateForm = useCallback(() => {
		let isValid = true;

		setSerialNumberError("");
		setCourseNameError("");
		setStandardError("");
		setClientOrganizationError("");
		setCategoryError("");
		setFromDateError("");
		setToDateError("");
		setStateError("");
		setTotalParticipantsError("");
		setOrderError("");

		// Serial number can be empty, so skip validation for it

		if (!course_name) {
			setCourseNameError("Course Name is required");
			isValid = false;
		}
		if (!standard) {
			setStandardError("Standard is required");
			isValid = false;
		}
		if (!client_organization) {
			setClientOrganizationError("Client Organization is required");
			isValid = false;
		}
		if (!category) {
			setCategoryError("Category is required");
			isValid = false;
		}
		if (!from_date) {
			setFromDateError("From date is required");
			isValid = false;
		}
		if (!to_date) {
			setToDateError("To date is required");
			isValid = false;
		}
		if (!state) {
			setStateError("State is required");
			isValid = false;
		}
		if (!total_participants || isNaN(total_participants)) {
			setTotalParticipantsError("Total Participants must be a valid number");
			isValid = false;
		}
		if (!order || isNaN(order)) {
			setOrderError("Order must be a valid number");
			isValid = false;
		}

		return isValid;
	}, [
		course_name,
		standard,
		client_organization,
		category,
		from_date,
		to_date,
		state,
		total_participants,
		order,
	]);

	// Form submission handler
	const handleSubmit = useCallback(
		async (e) => {
			e.preventDefault();

			if (!validateForm()) {
				return;
			}

			setLoading(true);
			dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: true } });

			try {
				const sendData = {
					serial_number,
					course_name,
					standard,
					client_organization,
					category,
					from_date,
					to_date,
					state,
					total_participants,
					order,
				};

				const res = await postDataAPI(
					"createSkillDevelopment",
					sendData,
					auth.token
				);

				if (res.status === 201) {
					alert("Upload Successful!!!");
					window.location.reload();
				}
			} catch (err) {
				console.error("Error creating skill development:", err);
				dispatch({
					type: GLOBALTYPES.ALERT,
					payload: {
						error:
							err.response?.data?.msg || "Error creating skill development",
					},
				});
			} finally {
				setLoading(false);
				dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: false } });
			}
		},
		[
			serial_number,
			course_name,
			standard,
			client_organization,
			category,
			from_date,
			to_date,
			state,
			total_participants,
			order,
			validateForm,
			auth.token,
			dispatch,
		]
	);

	// Edit handler
	const handleEdit = useCallback(
		(skill) => {
			setEditMode(true);
			setEditId(skill.id);
			setSerialNumber(skill.serial_number);
			setCourseName(skill.course_name);
			setStandard(skill.standard);
			setClientOrganization(skill.client_organization);
			setCategory(skill.category);
			setFromDate(skill.from_date);
			setToDate(skill.to_date);
			setState(skill.state);
			setTotalParticipants(skill.total_participants);
			setOrder(skill.order);
			setShow(true);
		},
		[
			setEditMode,
			setEditId,
			setSerialNumber,
			setCourseName,
			setStandard,
			setClientOrganization,
			setCategory,
			setFromDate,
			setToDate,
			setState,
			setTotalParticipants,
			setOrder,
			setShow,
		]
	);

	// Update handler
	const handleUpdate = useCallback(
		async (e) => {
			e.preventDefault();
			if (!validateForm()) return;
			setLoading(true);
			dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: true } });
			try {
				const sendData = {
					serial_number,
					course_name,
					standard,
					client_organization,
					category,
					from_date,
					to_date,
					state,
					total_participants,
					order,
				};
				const res = await postDataAPI(
					`updateSkillDevelopment/${editId}`,
					sendData,
					auth.token
				);
				if (res.status === 200) {
					alert("Update Successful!");
					window.location.reload();
				}
			} catch (err) {
				console.error("Error updating skill development:", err);
				dispatch({
					type: GLOBALTYPES.ALERT,
					payload: {
						error:
							err.response?.data?.msg || "Error updating skill development",
					},
				});
			} finally {
				setLoading(false);
				dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: false } });
			}
		},
		[
			serial_number,
			course_name,
			standard,
			client_organization,
			category,
			from_date,
			to_date,
			state,
			total_participants,
			order,
			editId,
			validateForm,
			auth.token,
			dispatch,
		]
	);

	// Fetch skill development records
	useEffect(() => {
		const fetchSkills = async () => {
			try {
				dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: true } });
				const res = await getDataAPI(`getAllSkillDevelopments`);
				setSkills(res.data || []);
			} catch (err) {
				console.error("Error fetching skill development records:", err);
				dispatch({
					type: GLOBALTYPES.ALERT,
					payload: { error: "Failed to fetch skill development records" },
				});
			} finally {
				dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: false } });
			}
		};

		fetchSkills();
	}, [dispatch]);

	// Delete handler
	const handleDelete = useCallback(
		async (skill) => {
			dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: true } });

			try {
				const res = await getDataAPI(
					`deleteSkillDevelopment/${skill.id}`,
					auth.token
				);

				if (res.status === 200) {
					window.location.reload();
				}
			} catch (err) {
				console.error("Error deleting skill development record:", err);
				dispatch({
					type: GLOBALTYPES.ALERT,
					payload: {
						error:
							err.response?.data?.msg ||
							"Error deleting skill development record",
					},
				});
			} finally {
				dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: false } });
			}
		},
		[auth.token, dispatch]
	);

	// Memoized table rows
	const tableRows = useMemo(() => {
		if (skills.length === 0) {
			return (
				<tr>
					<td colSpan="11" style={{ textAlign: "center" }}>
						No data available
					</td>
				</tr>
			);
		}

		return skills.skills.map((val, index) => (
			<tr key={val.id || index}>
				<td>{val.serial_number}</td>
				<td>{val.course_name}</td>
				<td>{val.standard}</td>
				<td>{val.client_organization}</td>
				<td>{val.category}</td>
				<td>{val.from_date}</td>
				<td>{val.to_date}</td>
				<td>{val.state}</td>
				<td>{val.total_participants}</td>
				<td>{val.order}</td>
				<td>
					<Button
						style={DELETE_BUTTON_STYLE}
						className="btn btn-danger mt-2"
						onClick={() => handleDelete(val)}>
						<i className="fa fa-trash" aria-hidden="true"></i>
					</Button>
					<Button
						style={{
							background: "#36BB7D",
							fontSize: "13px",
							borderRadius: "4px",
							border: "none",
							marginLeft: "8px",
						}}
						className="btn btn-success mt-2"
						onClick={() => handleEdit(val)}>
						<i className="fa fa-edit" aria-hidden="true"></i>
					</Button>
				</td>
			</tr>
		));
	}, [skills, handleDelete, handleEdit]);

	if (!auth.user) {
		return <LoginRequest />;
	}

	return (
		<>
			<Adminnavbar />
			<div style={CONTAINER_STYLE}>
				<div className="button-format">
					<div className="dashboard-text">Skill Development Management</div>
					<button className="addinstructor-btn" onClick={handleShow}>
						+ ADD
					</button>

					<Modal show={show} onHide={handleClose}>
						<div className="js">
							<div className="x-style" onClick={handleClose}>
								<span style={{ cursor: "pointer" }}>X</span>
							</div>
							<div className="js-boxIn">
								<div className="js-boxIn-content">
									<div className="ai-greenbox">
										<div>
											<h4 className="js-greenbox-heading">Skill Development</h4>
										</div>
										<div>
											<form onSubmit={editMode ? handleUpdate : handleSubmit}>
												<div className="js-inputbox mb-4">
													<span className="js-inputbox-input">
														<input
															type="number"
															value={serial_number}
															placeholder="Serial Number"
															onChange={(e) => setSerialNumber(e.target.value)}
														/>
													</span>
												</div>
												<p
													style={{
														visibility: serialNumberError
															? "visible"
															: "hidden",
													}}
													className="error-message">
													{serialNumberError}
												</p>

												<div className="js-inputbox">
													<span className="js-inputbox-input">
														<input
															type="text"
															value={course_name}
															placeholder="Course Name"
															onChange={(e) => setCourseName(e.target.value)}
														/>
													</span>
												</div>
												<p
													style={{
														visibility: courseNameError ? "visible" : "hidden",
													}}
													className="error-message">
													{courseNameError || "Course Name is required."}
												</p>

												<div className="js-inputbox">
													<span className="js-inputbox-input">
														<input
															type="text"
															value={standard}
															placeholder="Standard"
															onChange={(e) => setStandard(e.target.value)}
														/>
													</span>
												</div>
												<p
													style={{
														visibility: standardError ? "visible" : "hidden",
													}}
													className="error-message">
													{standardError || "Standard is required."}
												</p>

												<div className="js-inputbox">
													<span className="js-inputbox-input">
														<input
															type="text"
															value={client_organization}
															placeholder="Client Organization"
															onChange={(e) =>
																setClientOrganization(e.target.value)
															}
														/>
													</span>
												</div>
												<p
													style={{
														visibility: clientOrganizationError
															? "visible"
															: "hidden",
													}}
													className="error-message">
													{clientOrganizationError ||
														"Client Organization is required."}
												</p>

												<div className="js-inputbox">
													<span className="js-inputbox-input">
														<input
															type="text"
															value={category}
															placeholder="Category"
															onChange={(e) => setCategory(e.target.value)}
														/>
													</span>
												</div>
												<p
													style={{
														visibility: categoryError ? "visible" : "hidden",
													}}
													className="error-message">
													{categoryError || "Category is required."}
												</p>

												<div className="js-inputbox">
													<span className="js-inputbox-input">
														<input
															type="text"
															value={from_date}
															placeholder="From Date"
															onChange={(e) => setFromDate(e.target.value)}
														/>
													</span>
												</div>
												<p
													style={{
														visibility: fromDateError ? "visible" : "hidden",
													}}
													className="error-message">
													{fromDateError || "From Date is required."}
												</p>

												<div className="js-inputbox">
													<span className="js-inputbox-input">
														<input
															type="text"
															value={to_date}
															placeholder="To Date"
															onChange={(e) => setToDate(e.target.value)}
														/>
													</span>
												</div>
												<p
													style={{
														visibility: toDateError ? "visible" : "hidden",
													}}
													className="error-message">
													{toDateError || "To Date is required."}
												</p>

												<div className="js-inputbox">
													<span className="js-inputbox-input">
														<input
															type="text"
															value={state}
															placeholder="State"
															onChange={(e) => setState(e.target.value)}
														/>
													</span>
												</div>
												<p
													style={{
														visibility: stateError ? "visible" : "hidden",
													}}
													className="error-message">
													{stateError || "State is required."}
												</p>

												<div className="js-inputbox">
													<span className="js-inputbox-input">
														<input
															type="number"
															value={total_participants}
															placeholder="Total Participants"
															onChange={(e) =>
																setTotalParticipants(e.target.value)
															}
														/>
													</span>
												</div>
												<p
													style={{
														visibility: totalParticipantsError
															? "visible"
															: "hidden",
													}}
													className="error-message">
													{totalParticipantsError ||
														"Total Participants is required."}
												</p>

												<div className="js-inputbox">
													<span className="js-inputbox-input">
														<input
															type="number"
															value={order}
															placeholder="Order"
															onChange={(e) => setOrder(e.target.value)}
														/>
													</span>
												</div>
												<p
													style={{
														visibility: orderError ? "visible" : "hidden",
													}}
													className="error-message">
													{orderError || "Order is required."}
												</p>

												<button
													type="submit"
													className="js-submitbtn"
													disabled={loading}>
													{loading
														? editMode
															? "UPDATING..."
															: "SUBMITTING..."
														: editMode
														? "UPDATE"
														: "SUBMIT"}
												</button>
											</form>
										</div>
									</div>
								</div>
							</div>
						</div>
					</Modal>
				</div>
				<div>
					<div className="row format">
						<Sidenavbar />
						<div className="col-8 d-flex justify-content-center mt-5">
							<h1
								style={{
									color: "#36BB7D",
									fontWeight: 600,
									fontSize: "22px",
								}}>
								Scroll down to view the table
							</h1>
						</div>
						<div className="col-12 mt-5">
							<div className="table-responsive">
								<Table bordered striped style={{ textAlign: "center" }}>
									<thead id="pl">
										<tr>
											<th>Serial Number</th>
											<th>Course Name</th>
											<th>Standard</th>
											<th>Client Organization</th>
											<th>Category</th>
											<th>From</th>
											<th>To</th>
											<th>State</th>
											<th>Total Participants</th>
											<th>Order</th>
											<th>Action</th>
										</tr>
									</thead>
									<tbody>{tableRows}</tbody>
								</Table>
							</div>
						</div>
					</div>
				</div>
			</div>
			<Footer />
		</>
	);
};

export default SkillDevelopmentManagement;
